import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Eye, Paperclip, UserSearch } from 'lucide-react';
import { useCandidatesQuery, useJobsQuery, useOpenSigned } from '@/client/api/hr/records';
import { CANDIDATE_STAGES, CANDIDATE_STAGE_LABEL, type CandidateStageV2 } from '@/client/types/hr';
import { HrPageShell } from './components/HrPageShell';
import { EmptyState, RowMenu, StatusBadge } from './components/hr-ui';
import { formatDay } from './components/leave-format';

const filterClass = 'h-9 rounded-md border border-gray-200 bg-white px-3 text-sm outline-none ring-blue-500 focus:ring-2';
const STAGE_BADGE: Record<CandidateStageV2, string> = {
  applied: 'bg-gray-100 text-gray-700',
  screen: 'bg-sky-50 text-sky-700',
  interview: 'bg-violet-50 text-violet-700',
  offer: 'bg-amber-50 text-amber-800',
  hired: 'bg-emerald-50 text-emerald-700',
  rejected: 'bg-red-50 text-red-700',
};

/** Every candidate across every posting. Work on one role from its posting page. */
export default function CandidatesPage() {
  const navigate = useNavigate();
  const candQ = useCandidatesQuery();
  const jobs = useJobsQuery().data ?? [];
  const open = useOpenSigned('hr');
  const [stage, setStage] = useState<CandidateStageV2 | 'all'>('all');
  const [q, setQ] = useState('');
  const cands = candQ.data ?? [];
  const jobName = useMemo(() => new Map(jobs.map((j) => [j.id, j.title])), [jobs]);
  const shown = cands.filter(
    (c) => (stage === 'all' || c.stage === stage) && (!q || `${c.name} ${c.email}`.toLowerCase().includes(q.toLowerCase())),
  );

  return (
    <HrPageShell
      mock={false}
      title="Candidates"
      description="Everyone who has applied, across all postings."
      crumbs={[{ label: 'Human Resource', href: '/admin/human-resource' }, { label: 'Recruiting' }, { label: 'Candidates' }]}
      icon={UserSearch}
      stats={CANDIDATE_STAGES.filter((s) => s !== 'rejected').map((s) => ({ key: s, label: CANDIDATE_STAGE_LABEL[s], value: cands.filter((c) => c.stage === s).length }))}
    >
      <div className="mb-4 flex flex-wrap gap-3">
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name or email" className={`${filterClass} w-64`} />
        <select value={stage} onChange={(e) => setStage(e.target.value as CandidateStageV2 | 'all')} className={filterClass}>
          <option value="all">Every stage</option>
          {CANDIDATE_STAGES.map((s) => (
            <option key={s} value={s}>
              {CANDIDATE_STAGE_LABEL[s]}
            </option>
          ))}
        </select>
      </div>
      {candQ.isError ? (
        <EmptyState label={`Could not load candidates: ${(candQ.error as Error)?.message ?? 'unknown error'}`} />
      ) : candQ.isLoading ? (
        <EmptyState label="Loading candidates…" />
      ) : shown.length === 0 ? (
        <EmptyState label={cands.length ? 'No candidates match.' : 'No candidates yet. Add them from a job posting.'} />
      ) : (
        <div className="overflow-x-auto rounded-xl border border-gray-100 bg-white shadow-sm">
          <table className="w-full text-left text-sm">
            <thead className="border-b border-gray-100 bg-gray-50 text-xs uppercase tracking-wide text-gray-500">
              <tr>
                <th className="px-4 py-3 font-medium">Candidate</th>
                <th className="px-4 py-3 font-medium">Role</th>
                <th className="px-4 py-3 font-medium">Applied</th>
                <th className="px-4 py-3 font-medium">Stage</th>
                <th className="px-4 py-3" />
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {shown.map((c) => (
                <tr key={c.id} className="cursor-pointer hover:bg-gray-50" onClick={() => navigate(`/admin/human-resource/recruiting/postings/${c.jobPostingId}`)}>
                  <td className="px-4 py-3">
                    <p className="font-medium text-gray-900">{c.name}</p>
                    <p className="text-xs text-gray-400">{c.email || c.phone || '—'}</p>
                  </td>
                  <td className="px-4 py-3 text-gray-600">{jobName.get(c.jobPostingId) ?? '—'}</td>
                  <td className="px-4 py-3 text-gray-600">{c.appliedAt ? formatDay(c.appliedAt) : '—'}</td>
                  <td className="px-4 py-3">
                    <StatusBadge label={CANDIDATE_STAGE_LABEL[c.stage]} className={STAGE_BADGE[c.stage]} />
                  </td>
                  <td className="px-4 py-3 text-right">
                    <RowMenu
                      items={[
                        { label: 'Open posting', icon: Eye, onSelect: () => navigate(`/admin/human-resource/recruiting/postings/${c.jobPostingId}`) },
                        { label: 'Open CV', icon: Paperclip, hidden: !c.hasCv, onSelect: () => open(`/candidates/${c.id}/cv`).catch((e) => toast.error((e as Error).message)) },
                      ]}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </HrPageShell>
  );
}
